import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../lib/api";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [terms, setTerms] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match"); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (!terms) { setError("Please accept the terms and privacy policy"); return; }
    setLoading(true);
    try {
      await register(name, email, password);
      navigate(params.get("plan") ? `/pricing?plan=${params.get("plan")}` : "/pricing");
    } catch (err) {
      setError(errMsg(err, "Registration failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="register-page">
      <div className="flex flex-col justify-center px-6 md:px-16 lg:px-24 py-12">
        <Logo />
        <h1 className="mt-12 font-serif text-3xl md:text-4xl font-bold tracking-tight" data-testid="register-title">Create your account</h1>
        <p className="mt-2 text-sm text-muted-foreground">Step 1 of 2 — account details. You'll choose your plan next.</p>
        <form onSubmit={submit} className="mt-8 space-y-5 max-w-md">
          {error && <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm px-4 py-3 rounded-md" data-testid="register-error">{error}</div>}
          <div className="space-y-2">
            <Label htmlFor="name">Full name</Label>
            <Input id="name" data-testid="register-name-input" required autoComplete="name"
              value={name} onChange={(e) => setName(e.target.value)} placeholder="Alex Khan" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" data-testid="register-email-input" type="email" required autoComplete="email"
              value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" data-testid="register-password-input" type="password" required autoComplete="new-password"
              value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm">Confirm password</Label>
            <Input id="confirm" data-testid="register-confirm-input" type="password" required autoComplete="new-password"
              value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat your password" />
          </div>
          <div className="flex items-start gap-3">
            <Checkbox id="terms" data-testid="register-terms-checkbox" checked={terms} onCheckedChange={setTerms} />
            <Label htmlFor="terms" className="text-sm font-normal leading-relaxed text-muted-foreground">
              I accept the <Link to="/legal/terms" className="text-primary hover:underline">terms &amp; conditions</Link> and{" "}
              <Link to="/legal/privacy" className="text-primary hover:underline">privacy policy</Link>.
            </Label>
          </div>
          <Button type="submit" data-testid="register-submit-btn" disabled={loading} className="w-full">
            {loading ? "Creating account…" : "Continue to plans"}
          </Button>
          <p className="text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" data-testid="goto-login-link" className="text-primary font-medium hover:underline">Log in</Link>
          </p>
        </form>
      </div>
      <div className="hidden lg:block relative">
        <img src="https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="Physician associate in medical uniform with stethoscope in a hospital corridor"
          className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-primary/60" />
        <div className="absolute bottom-16 left-16 right-16 text-primary-foreground">
          <p className="font-serif text-2xl font-bold leading-snug">PA-specific SBA practice, designed around UK PA education and the PARA.</p>
        </div>
      </div>
    </div>
  );
}
